import { Mediator, payload } from "modular-openscriptjs";
import { acknowledge, approved, eParse, inform } from "../helpers";
import { api, STRINGS } from "../declarations";
import { $e } from "../ojs.events";

export default class BranchMediator extends Mediator {
    $$branch = {
        staff: {
            needs: {
                creation: (ed, event) => this.saveStaff(ed, event),
                deletion: (ed, event) => this.deleteStaff(ed, event),
            },
            invitation: {
                needs: {
                    creation: (ed, event) => this.sendInvitation(ed, event),
                    cancellation: (ed, event) =>
                        this.cancelInvitation(ed, event),
                },
            },
        },
        needs: {
            deletion: (ed, event) => this.deleteBranch(ed, event),
            creation: (ed, event) => this.saveBranch(ed, event),
            update: (ed, event) => this.updateBranch(ed, event),
            toggleOnline: (ed, event) => this.toggleOnline(ed, event),
            bulkToggleOnline: (ed, event) => this.bulkToggleOnline(ed, event),
        },
    };

    async saveBranch(ed, event) {
        const { message } = eParse(ed);

        const res = await api.post(
            `companies/${message.businessId}/branches`,
            message,
        );

        if (!res) {
            inform("Failed to save branch", STRINGS.ERROR);
            return this.send($e.branch.has.creationError, ed);
        }

        inform("Branch saved successfully", STRINGS.SUCCESS);
        this.send($e.branch.created, payload({ branch: res.data.branch }));
    }

    async deleteBranch(ed, event) {
        if (
            !approved(event, ed, "Are you sure you want to delete this branch?")
        )
            return;

        const { message } = eParse(ed);

        const res = await api.delete(
            `companies/${message.businessId}/branches/${message.id}`,
            {
                organization: {
                    id: message.businessId,
                    type: "Company",
                },
            },
        );

        if (!res) {
            inform("Failed to delete branch", STRINGS.ERROR);
            return this.send($e.branch.has.deletionError, ed);
        }

        inform("Branch deleted successfully", STRINGS.SUCCESS);
        this.send($e.branch.deleted, payload({ branch: res.data.branch }));
    }

    async updateBranch(ed, event) {
        const { message } = eParse(ed);

        message.organization = {
            id: message.businessId,
            type: "Company",
        };

        const res = await api.put(
            `companies/${message.businessId}/branches/${message.id}`,
            message,
        );

        if (!res) {
            inform("Failed to update branch", STRINGS.ERROR);
            return this.send($e.branch.has.updateError, ed);
        }

        inform("Branch updated successfully", STRINGS.SUCCESS);
        this.send($e.branch.updated, payload({ branch: res.data.branch }));
    }

    async sendInvitation(ed, event) {
        const { message } = eParse(ed);

        const res = await api.post(`branches/${message.branchId}/invitations`, {
            identifier: message.identifier,
            role: message.role,
            company_id: message.businessId,
        });

        if (!res) {
            inform("Failed to send invitation", STRINGS.ERROR);
            return this.send($e.branch.staff.invitation.has.creationError, ed);
        }

        inform("Invitation sent successfully!", STRINGS.SUCCESS);
        this.send(
            $e.branch.staff.invitation.created,
            payload({ invitation: res.data.invitation }),
        );
    }

    async cancelInvitation(ed, event) {
        if (
            !approved(
                event,
                ed,
                "Are you sure you want to cancel this invitation?",
            )
        )
            return;

        const { message } = eParse(ed);

        let res = await api.delete(
            `branches/${message.branchId}/invitations/${message.id}`,
            {
                organization: {
                    id: message.businessId,
                    type: "Company",
                },
            },
        );

        if (!res) {
            inform("Failed to cancel invitation", STRINGS.ERROR);
            return this.send(
                $e.branch.staff.invitation.has.cancellationError,
                ed,
            );
        }

        inform("Invitation cancelled", STRINGS.SUCCESS);

        this.send(
            $e.branch.staff.invitation.cancelled,
            payload({ invitation: res.data.invitation }),
        );
    }

    async deleteStaff(ed, event) {
        if (
            !approved(
                event,
                ed,
                "Are you sure you want to remove this staff member?",
            )
        )
            return;

        const { message } = eParse(ed);

        const res = await api.delete(
            `branches/${message.branchId}/staff/${message.id}`,
            {
                organization: {
                    id: message.businessId,
                    type: "Company",
                },
            },
        );

        if (!res) {
            inform("Failed to remove staff member", STRINGS.ERROR);
            return this.send($e.branch.staff.has.deletionError, ed);
        }

        inform("Staff member removed successfully", STRINGS.SUCCESS);
        this.send($e.branch.staff.deleted, payload({ id: message.id }));
    }

    async toggleOnline(ed, event) {
        const { message } = eParse(ed);

        const res = await api.put(
            `companies/${message.businessId}/branches/${message.id}/toggle-online`,
            {
                is_online: message.is_online,
                organization: { id: message.businessId, type: "Company" },
            },
        );

        if (!res) {
            inform("Failed to update branch status", STRINGS.ERROR);
            return this.send($e.branch.has.updateError, ed);
        }

        inform("Branch status updated successfully", STRINGS.SUCCESS);
        this.send($e.branch.updated, payload({ branch: res.data.branch }));
    }

    async bulkToggleOnline(ed, event) {
        const { message } = eParse(ed);

        if (
            !message.is_online &&
            !approved(
                event,
                ed,
                "This will take ALL of your outlets offline. Customers won't be able to find your business or place new orders until you switch back online. Continue?",
            )
        )
            return;

        const res = await api.put(
            `companies/${message.businessId}/branches/toggle-online`,
            {
                is_online: message.is_online,
                organization: { id: message.businessId, type: "Company" },
            },
        );

        if (!res) {
            inform("Failed to update branches status", STRINGS.ERROR);
            return this.send($e.branch.has.updateError, ed);
        }

        inform(
            message.is_online
                ? "All branches are now online"
                : "All branches are now offline",
            STRINGS.SUCCESS,
        );

        acknowledge({
            title: message.is_online ? "You're Back Online" : "You're Offline",
            message: message.is_online
                ? "All your outlets are now online and accepting orders."
                : "All your outlets are now offline. Customers won't be able to place new orders until you switch back on.",
            close_text: "Got it",
        });

        this.send($e.branch.updated, payload({ is_online: message.is_online }));
    }
}
